/**
 * 48 コマ表の列ヘッダー行を貼り付ける (sticky) かどうかの判定。
 *
 * design/CLAUDE.md「`thead` の `position: sticky` は `overflow-x: auto` と併用
 * しない」節が実装手順まで規約化しており、参照実装は
 * `design/demand/forecast-today.html` (`.koma-table-wrap` の CSS と
 * `_dftSyncKomaTheadSticky`)。要点は 3 つ:
 *
 * 1. sticky は**いちばん近いスクロールコンテナ**に効く。表 wrap が
 *    `overflow-x: auto` だと (`overflow-y: visible` を併記しても CSS 仕様上
 *    `auto` に計算されるので) wrap 自身がコンテナになり、ページをスクロール
 *    しても列名は流れていく。だから収まっている間は wrap を
 *    `overflow: visible` にして、ページ側 (= ワークスペースの
 *    `overflow: auto`) に貼り付ける
 * 2. 横スクロールが実際に要る時は wrap を `overflow-x: auto` に戻し、
 *    **sticky は諦める**。横スクロール手段を失う方が害が大きい
 *    (参照列ヘッダーの「採用」ボタンが画面外で押せなくなる = AC-DFD-047 / 048)
 * 3. 貼り付き位置は上に重なる sticky 帯 (48 コマ表カードの見出しバー) の
 *    **実測高**。帯どうしの間に余白を空けない
 *
 * design は 1・2 をまとめて `@media (min-width: 1200px)` の中に閉じ込めており、
 * 1 カラム (stacked) では基底の `overflow-x: auto` に戻す = sticky しない。
 * 本実装も同じで、2 カラムでない時は現状の挙動をそのまま残す。
 *
 * 判定を純関数に切り出しているのは、happy-dom がレイアウトを持たず
 * (`getBoundingClientRect` が全部 0 / media query を評価しない)、貼り付き自体は
 * component test で観測できないため。同画面の `forecastChartHeightFor` と同じ作法。
 */

/**
 * 列ヘッダー行の重なり順。48 コマ表カードの見出しバー (z-index 5) の下、
 * セル (0) の上。design `forecast-today.html` の値と同じ。
 */
export const KOMA_THEAD_STICKY_Z_INDEX = 4

/**
 * 表が wrap に収まらない (= 横スクロールが要る) か。
 *
 * `+1` は fractional pixel の丸め許容 (design 側の判定と同値。
 * `MasterDataTable/stickyActionColumn.ts` も同じ許容を持つ)。
 *
 * 比べるのは **table 自身の実幅**と wrap の内寸。wrap の `scrollWidth` を使うと、
 * 一度 `overflow-x: auto` にした後は wrap の内寸と一致して二度と false に
 * 戻らないラッチになる (design はクラスを外してから測り直すことで回避している)。
 * 測定対象を table 側に置けばそのラッチが構造的に起きない。
 */
export function hasKomaTableHorizontalOverflow(
  tableScrollWidth: number,
  wrapClientWidth: number,
): boolean {
  if (tableScrollWidth <= 0 || wrapClientWidth <= 0) return true
  return tableScrollWidth > wrapClientWidth + 1
}

/**
 * 列ヘッダー行を貼り付けるか。
 *
 * 2 カラム表示 かつ 横スクロールが要らない 時だけ true。計測できていない間
 * (初期描画・happy-dom) は `hasHorizontalOverflow` が true に倒れるので、
 * **安全側 = 現状の挙動 (横スクロール可能・非 sticky)** から始まる。
 */
export function shouldStickKomaThead({
  hasHorizontalOverflow,
  isTwoColumn,
}: {
  hasHorizontalOverflow: boolean
  isTwoColumn: boolean
}): boolean {
  return isTwoColumn && !hasHorizontalOverflow
}

/**
 * 表 wrap の overflow。貼り付ける時だけ `visible` にしてページ側へ委ねる。
 * `overflow-y` は常に `visible` (縦は元からページがスクロールする)。
 */
export function komaTableWrapOverflowStyle(
  isSticky: boolean,
): Pick<React.CSSProperties, 'overflowX' | 'overflowY'> {
  return {
    overflowX: isSticky ? 'visible' : 'auto',
    overflowY: 'visible',
  }
}

/** 列ヘッダーセルに重ねる sticky 指定。`topPx` は上に重なる帯の実測高。 */
export function komaTheadStickyStyle(topPx: number): React.CSSProperties {
  return {
    position: 'sticky',
    top: `${String(topPx)}px`,
    zIndex: KOMA_THEAD_STICKY_Z_INDEX,
  }
}
